import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, Library, Music, Sparkles, BookOpen, User, LogOut, Trophy } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import StickyPlayer from '@/components/StickyPlayer';
import FullScreenPlayer from '@/components/FullScreenPlayer';
import ReminderBanner from '@/components/ReminderBanner';
import PostMoodToast from '@/components/PostMoodToast';
import LegalFooter from '@/components/LegalFooter';
import { usePlayer } from '@/contexts/PlayerContext';

const tabs = [
    { to: '/app/home', label: 'Home', icon: Home, testId: 'tab-home' },
    { to: '/app/library', label: 'Library', icon: Library, testId: 'tab-library' },
    { to: '/app/music', label: 'Sounds', icon: Music, testId: 'tab-music' },
    { to: '/app/create', label: 'Create', icon: Sparkles, testId: 'tab-create' },
    { to: '/app/challenges', label: 'Arcs', icon: Trophy, testId: 'tab-challenges' },
    { to: '/app/journal', label: 'Journal', icon: BookOpen, testId: 'tab-journal' },
    { to: '/app/profile', label: 'You', icon: User, testId: 'tab-profile' },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { track } = usePlayer();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-background relative overflow-x-hidden" data-testid="app-layout">
            {/* Soft ambient blobs */}
            <div className="pointer-events-none fixed -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-sage/30 blur-3xl" aria-hidden />
            <div className="pointer-events-none fixed top-1/3 -right-32 w-[400px] h-[400px] rounded-full bg-dusty/40 blur-3xl" aria-hidden />
            <div className="pointer-events-none fixed -bottom-40 left-1/3 w-[420px] h-[420px] rounded-full bg-lavender-soft/40 blur-3xl" aria-hidden />

            {/* Desktop top bar */}
            <header className="hidden md:flex sticky top-0 z-30 qm-glass px-8 py-4 items-center justify-between">
                <Link to="/app/home" className="flex items-center gap-2" data-testid="logo-link">
                    <img
                        src="/qm-logo.png"
                        alt="Quiet Mind"
                        className="w-10 h-10 rounded-full object-cover bg-black shrink-0"
                    />
                    <span className="font-serif text-xl text-forest">Quiet Mind</span>
                </Link>
                <nav className="flex items-center gap-1">
                    {tabs.map((t) => (
                        <NavLink
                            key={t.to}
                            to={t.to}
                            data-testid={t.testId}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                                    isActive
                                        ? 'bg-forest text-cream shadow-sm'
                                        : 'text-forest hover:bg-sage/30'
                                }`
                            }
                        >
                            {t.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-forest-soft hidden lg:inline">Hi, {user?.name?.split(' ')[0]}</span>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="qm-btn-ghost flex items-center gap-2"
                        aria-label="Sign out"
                    >
                        <LogOut size={16} /> <span className="hidden lg:inline">Sign out</span>
                    </button>
                </div>
            </header>

            {/* Mobile top bar */}
            <header className="md:hidden sticky top-0 z-30 qm-glass px-5 py-3 flex items-center justify-between">
                <Link to="/app/home" className="flex items-center gap-2">
                    <img
                        src="/qm-logo.png"
                        alt="Quiet Mind"
                        className="w-8 h-8 rounded-full object-cover bg-black shrink-0"
                    />
                    <span className="font-serif text-lg text-forest">Quiet Mind</span>
                </Link>
                <button
                    onClick={handleLogout}
                    data-testid="mobile-logout-button"
                    aria-label="Sign out"
                    className="rounded-full p-2 text-forest hover:bg-sage/30"
                >
                    <LogOut size={18} />
                </button>
            </header>

            <main
                key={location.pathname}
                className={`relative z-10 max-w-6xl mx-auto px-5 md:px-8 pt-6 pb-32 md:pb-40 animate-fade-in`}
                data-testid="page-main"
            >
                {children}
                <LegalFooter className="mt-12" />
            </main>

            {/* Sticky player always above bottom tab bar */}
            <StickyPlayer />
            <FullScreenPlayer />
            <ReminderBanner />
            <PostMoodToast />

            {/* Mobile bottom tab bar */}
            <nav
                className="md:hidden fixed bottom-0 inset-x-0 z-30 qm-glass border-t border-white/40 px-2 pb-[env(safe-area-inset-bottom)]"
                data-testid="bottom-tab-bar"
                style={track ? { paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' } : {}}
            >
                <ul className="flex items-stretch justify-between">
                    {tabs.map((t) => {
                        const Icon = t.icon;
                        return (
                            <li key={t.to} className="flex-1">
                                <NavLink
                                    to={t.to}
                                    data-testid={`mobile-${t.testId}`}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center gap-0.5 py-2.5 mx-0.5 my-1 rounded-2xl transition-all duration-300 ${
                                            isActive ? 'bg-sage/40 text-forest' : 'text-forest/70 hover:text-forest'
                                        }`
                                    }
                                >
                                    <Icon size={20} />
                                    <span className="text-[10px] tracking-wide">{t.label}</span>
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        </div>
    );
}
